// 현장 목록 「달력」 보기 계산 — 한 달 격자(일요일 시작)·주마다 막대 줄 배치·고른 날 일정·이달 현장. 화면은 칸만 그린다.

import { addDays, type ChartRange, daysBetween, isBoardMonth, shiftMonth } from "./phase-chart";
import { PROJECT_STATUS_LABEL, type Project, type ProjectPhaseStatus, type ProjectStatus } from "./types";
import { projectNameOf, todayKst } from "./view";

export const CALENDAR_WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"] as const;

// 주 한 줄에 그리는 막대 줄 수 — 넘치면 날마다 「+n건」.
export const CALENDAR_MAX_LANES = 4;

// 현장 색 다섯 벌 — 이달 현장 순서대로 돌려 쓴다(화면이 톤 번호를 토큰 클래스로 바꾼다).
export const CALENDAR_TONE_COUNT = 5;

export type CalendarProject = Pick<
  Project,
  | "id"
  | "title"
  | "site_address"
  | "status"
  | "assignee_id"
  | "planned_start_date"
  | "planned_end_date"
  | "completion_date"
  | "started_at"
>;

// 공정 일정 참조 — 저장소 ProjectPhaseScheduleRef 의 필요한 필드만(구조적으로 받는다).
export interface CalendarPhase {
  project_id: string;
  phase_code: string;
  start_date: string | null;
  end_date: string | null;
  id?: string;
  phase_id?: string;
  sort_order?: number;
  // 보류면 막대·패널에 「보류」 를 달고 담당자 그날 현장 수에서 뺀다.
  status?: ProjectPhaseStatus;
}

export type CalendarEventKind = "start" | "end" | "phase";

export interface CalendarDay {
  date: string;
  day: number;
  // 「10월 3일 (토)」.
  title: string;
  // 0 = 일 … 6 = 토.
  weekday: number;
  inMonth: boolean;
  today: boolean;
  selected: boolean;
  // 공휴일 이름(이름을 모르면 「공휴일」) · 아니면 null.
  holiday: string | null;
}

export interface CalendarSegment {
  key: string;
  projectId: string;
  kind: CalendarEventKind;
  // 막대 글자 — 공정은 「현장 · 공정」, 착공·준공은 「▶ 착공 · 현장」(착공 전은 「▶ 착공 예정 · 현장」).
  label: string;
  tone: number;
  // 그 주 안의 칸(0 = 일요일)과 칸 수.
  col: number;
  span: number;
  lane: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
  // 보류 공정 — 화면이 흐리게 그린다.
  held: boolean;
}

export interface CalendarMore {
  col: number;
  count: number;
}

export interface CalendarWeek {
  days: CalendarDay[];
  segments: CalendarSegment[];
  more: CalendarMore[];
}

export interface CalendarDayItem {
  key: string;
  projectId: string;
  title: string;
  owner: string;
  kind: CalendarEventKind;
  // 「착공 예정일」 · 「도장 3 / 5일째 · 마지막 날」.
  text: string;
  tone: number;
  held: boolean;
}

export interface CalendarOwnerLoad {
  name: string;
  count: number;
  // 막대 길이 0~1 — 이달 가장 많은 담당자의 현장 수가 1.
  ratio: number;
  // 하루에 두 곳 이상.
  busy: boolean;
}

export interface CalendarSite {
  id: string;
  title: string;
  status: ProjectStatus;
  owner: string;
  tone: number;
}

export interface CalendarSelected {
  date: string;
  // 「10월 3일 (토)」.
  title: string;
  today: boolean;
  holiday: string | null;
  items: CalendarDayItem[];
  owners: CalendarOwnerLoad[];
}

export interface CalendarAgendaDay {
  date: string;
  title: string;
  today: boolean;
  holiday: string | null;
  items: CalendarDayItem[];
}

export interface SiteCalendar {
  month: string;
  // 「2026년 10월」.
  monthTitle: string;
  prevMonth: string;
  nextMonth: string;
  todayMonth: string;
  isTodayMonth: boolean;
  maxLanes: number;
  weeks: CalendarWeek[];
  selected: CalendarSelected;
  // 이달에 일정이 하루라도 걸린 현장.
  sites: CalendarSite[];
  // 폰 폭용 — 이달 날짜 중 일정 있는 날만.
  agenda: CalendarAgendaDay[];
}

export interface SiteCalendarInput {
  projects: readonly CalendarProject[];
  phases: readonly CalendarPhase[];
  // 공정 코드 → 그 회사에게 보이는 이름.
  phaseName: (code: string) => string;
  // 담당자 id → 이름(모르면 「미지정」).
  ownerName?: (assigneeId: string) => string | null | undefined;
  // `?month=` 원문 — 모양이 아니면 오늘 달.
  month: string | string[] | null | undefined;
  // `?day=` 원문 — 격자 밖·모양이 아니면 오늘(이달이 아니면 1일).
  day?: string | string[] | null | undefined;
  today: string;
  // 날짜 집합 또는 날짜 → 이름.
  holidays?: Iterable<string> | ReadonlyMap<string, string>;
  maxLanes?: number;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const UNASSIGNED = "미지정";

const STATUS_ORDER: Record<ProjectStatus, number> = { IN_PROGRESS: 0, SCHEDULED: 1, WARRANTY: 2, CLOSED: 3 };

const first = (v: string | string[] | null | undefined): string | null => (Array.isArray(v) ? (v[0] ?? null) : (v ?? null));

const isRealDate = (d: string): boolean =>
  DATE_RE.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`)) && addDays(d, 0) === d;

// `?month=` — YYYY-MM(01~12) 만 받고 아니면 오늘 달.
export const parseCalendarMonth = (value: string | string[] | null | undefined, today: string): string => {
  const v = first(value);
  return isBoardMonth(v) ? v : today.slice(0, 7);
};

export const daysInMonth = (month: string): number => daysBetween(`${month}-01`, `${shiftMonth(month, 1)}-01`);

const weekdayOf = (date: string): number => new Date(`${date}T00:00:00Z`).getUTCDay();

// 달력에 드는 현장 상태 — 기본은 시공대기·시공중·유지보수, 상태를 고르면 그 상태만.
export const SITE_CALENDAR_STATUSES: readonly ProjectStatus[] = ["SCHEDULED", "IN_PROGRESS", "WARRANTY"];

export const calendarStatusesOf = (status: ProjectStatus | null): ProjectStatus[] =>
  status === null ? [...SITE_CALENDAR_STATUSES] : [status];

// 격자 범위(앞뒤 달 칸 포함) — 공휴일을 받아 올 해를 정할 때 쓴다.
export const siteCalendarRange = (month: string): ChartRange => {
  const monthStart = `${month}-01`;
  const monthEnd = addDays(monthStart, daysInMonth(month) - 1);
  const start = addDays(monthStart, -weekdayOf(monthStart));
  const end = addDays(monthEnd, 6 - weekdayOf(monthEnd));
  return { start, end, days: daysBetween(start, end) + 1 };
};

// 머리줄 — 「시공중 3 · 시공대기 2 · 유지보수 1」(이달 현장 상태별, 0 은 뺀다).
export const siteCalendarMetaOf = (sites: readonly Pick<CalendarSite, "status">[]): string => {
  const order: ProjectStatus[] = ["IN_PROGRESS", "SCHEDULED", "WARRANTY", "CLOSED"];
  const parts = order.flatMap((st) => {
    const n = sites.filter((s) => s.status === st).length;
    return n > 0 ? [`${PROJECT_STATUS_LABEL[st]} ${n}`] : [];
  });
  return parts.length > 0 ? parts.join(" · ") : "이달 잡힌 현장 없음";
};

const dayTitle = (date: string): string =>
  `${Number(date.slice(5, 7))}월 ${Number(date.slice(8, 10))}일 (${CALENDAR_WEEKDAYS[weekdayOf(date)]})`;

interface RawEvent {
  key: string;
  project: CalendarProject;
  kind: CalendarEventKind;
  name: string;
  from: string;
  to: string;
  order: number;
  held: boolean;
}

// 착공 칸 — 착공한 현장은 실제 착공일(KST)에 「착공」, 착공 전은 예정일에 「착공 예정일」.
const startOf = (project: CalendarProject): { date: string | null; text: string } => {
  if (project.started_at !== null) return { date: todayKst(new Date(project.started_at)), text: "착공" };
  return { date: project.planned_start_date, text: project.status === "SCHEDULED" ? "착공 예정일" : "착공" };
};

// 현장 하나의 일정 — 착공(실제 착공일, 없으면 착공 예정일) · 준공(준공일, 없으면 준공 예정일) · 공정(계획 시작~끝). 날짜 없는 것은 뺀다.
const eventsOf = (
  project: CalendarProject,
  phases: readonly CalendarPhase[],
  phaseName: (code: string) => string
): RawEvent[] => {
  const out: RawEvent[] = [];
  const d = startOf(project).date;
  if (d !== null && isRealDate(d)) {
    out.push({ key: `${project.id}:start`, project, kind: "start", name: "착공", from: d, to: d, order: -1, held: false });
  }
  const ordered = [...phases].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  ordered.forEach((p, i) => {
    if (p.start_date === null || p.end_date === null) return;
    if (!isRealDate(p.start_date) || !isRealDate(p.end_date) || p.start_date > p.end_date) return;
    out.push({
      key: `${project.id}:phase:${p.id ?? p.phase_id ?? p.phase_code}:${i}`,
      project,
      kind: "phase",
      name: phaseName(p.phase_code),
      from: p.start_date,
      to: p.end_date,
      order: i,
      held: p.status === "HOLD",
    });
  });
  const end = project.completion_date ?? project.planned_end_date;
  if (end !== null && isRealDate(end)) {
    out.push({ key: `${project.id}:end`, project, kind: "end", name: "준공", from: end, to: end, order: Number.MAX_SAFE_INTEGER, held: false });
  }
  return out;
};

const overlaps = (e: RawEvent, from: string, to: string): boolean => e.from <= to && e.to >= from;

const holidayMap = (holidays: SiteCalendarInput["holidays"]): Map<string, string> => {
  const map = new Map<string, string>();
  if (!holidays) return map;
  if (holidays instanceof Map) {
    for (const [d, name] of holidays as ReadonlyMap<string, string>) map.set(d, name.trim() || "공휴일");
    return map;
  }
  for (const d of holidays as Iterable<string>) map.set(d, "공휴일");
  return map;
};

const itemText = (e: RawEvent, date: string): string => {
  if (e.kind === "start") return startOf(e.project).text;
  if (e.kind === "end") return e.project.completion_date !== null ? "준공일" : "준공 예정일";
  const n = daysBetween(e.from, date) + 1;
  const total = daysBetween(e.from, e.to) + 1;
  if (e.held) return `${e.name} 보류 · 계획 ${n} / ${total}일째`;
  return `${e.name} ${n} / ${total}일째${date === e.to ? " · 마지막 날" : ""}`;
};

// 격자 막대의 착공 이름 — 착공 전(패널 「착공 예정일」)은 「착공 예정」.
const segmentName = (e: RawEvent): string => (e.kind === "start" && startOf(e.project).text === "착공 예정일" ? "착공 예정" : e.name);

const segmentLabel = (e: RawEvent, continuesAfter: boolean): string => {
  const base = e.kind === "phase" ? `${projectNameOf(e.project)} · ${e.name}${e.held ? " 보류" : ""}` : `${e.kind === "start" ? "▶" : "■"} ${segmentName(e)} · ${projectNameOf(e.project)}`;
  return continuesAfter ? `${base} ›` : base;
};

export const buildSiteCalendar = (input: SiteCalendarInput): SiteCalendar => {
  const { today } = input;
  const maxLanes = input.maxLanes ?? CALENDAR_MAX_LANES;
  const month = parseCalendarMonth(input.month, today);
  const monthStart = `${month}-01`;
  const monthEnd = addDays(monthStart, daysInMonth(month) - 1);
  const { start: gridStart, end: gridEnd } = siteCalendarRange(month);
  const holidays = holidayMap(input.holidays);
  const ownerOf = (p: CalendarProject): string =>
    (p.assignee_id !== null ? input.ownerName?.(p.assignee_id) : null) || UNASSIGNED;

  const phasesBy = new Map<string, CalendarPhase[]>();
  for (const ph of input.phases) phasesBy.set(ph.project_id, [...(phasesBy.get(ph.project_id) ?? []), ph]);

  const all = input.projects.flatMap((p) => eventsOf(p, phasesBy.get(p.id) ?? [], input.phaseName));
  const inGrid = all.filter((e) => overlaps(e, gridStart, gridEnd));

  // 현장 순서 — 시공중 → 시공대기 → 유지보수 → 완료, 같으면 착공 예정일·이름. 이달 현장이 먼저 색을 받는다.
  const byOrder = (a: CalendarProject, b: CalendarProject): number =>
    STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
    (a.planned_start_date ?? "9999").localeCompare(b.planned_start_date ?? "9999") ||
    projectNameOf(a).localeCompare(projectNameOf(b)) ||
    a.id.localeCompare(b.id);
  const monthIds = new Set(all.filter((e) => overlaps(e, monthStart, monthEnd)).map((e) => e.project.id));
  const gridIds = new Set(inGrid.map((e) => e.project.id));
  const monthProjects = input.projects.filter((p) => monthIds.has(p.id)).sort(byOrder);
  const edgeProjects = input.projects.filter((p) => gridIds.has(p.id) && !monthIds.has(p.id)).sort(byOrder);
  const siteRank = new Map<string, number>();
  [...monthProjects, ...edgeProjects].forEach((p, i) => siteRank.set(p.id, i));
  const toneOf = (projectId: string): number => (siteRank.get(projectId) ?? 0) % CALENDAR_TONE_COUNT;

  const dayParam = first(input.day);
  const selectedDate =
    dayParam !== null && isRealDate(dayParam) && dayParam >= gridStart && dayParam <= gridEnd
      ? dayParam
      : today.slice(0, 7) === month
        ? today
        : monthStart;

  const dayOf = (date: string): CalendarDay => ({
    date,
    day: Number(date.slice(8, 10)),
    title: dayTitle(date),
    weekday: weekdayOf(date),
    inMonth: date >= monthStart && date <= monthEnd,
    today: date === today,
    selected: date === selectedDate,
    holiday: holidays.get(date) ?? null,
  });

  const weekCount = (daysBetween(gridStart, gridEnd) + 1) / 7;
  const weeks: CalendarWeek[] = [];
  for (let w = 0; w < weekCount; w += 1) {
    const ws = addDays(gridStart, w * 7);
    const we = addDays(ws, 6);
    const days = Array.from({ length: 7 }, (_, i) => dayOf(addDays(ws, i)));
    const segs = inGrid
      .filter((e) => overlaps(e, ws, we))
      .map((e) => {
        const s = e.from < ws ? ws : e.from;
        const t = e.to > we ? we : e.to;
        return { e, col: daysBetween(ws, s), span: daysBetween(s, t) + 1 };
      })
      .sort(
        (a, b) =>
          a.col - b.col ||
          b.span - a.span ||
          (siteRank.get(a.e.project.id) ?? 0) - (siteRank.get(b.e.project.id) ?? 0) ||
          a.e.order - b.e.order
      );
    // 줄 채우기 — 앞 막대가 끝난 다음 칸부터 같은 줄을 쓴다.
    const laneEnds: number[] = [];
    const hidden = [0, 0, 0, 0, 0, 0, 0];
    const segments: CalendarSegment[] = [];
    for (const g of segs) {
      let lane = 0;
      while (laneEnds[lane] !== undefined && laneEnds[lane]! >= g.col) lane += 1;
      laneEnds[lane] = g.col + g.span - 1;
      if (lane >= maxLanes) {
        for (let c = g.col; c < g.col + g.span; c += 1) hidden[c] = (hidden[c] ?? 0) + 1;
        continue;
      }
      const continuesBefore = g.e.from < ws;
      const continuesAfter = g.e.to > we;
      segments.push({
        key: `${g.e.key}:${ws}`,
        projectId: g.e.project.id,
        kind: g.e.kind,
        label: segmentLabel(g.e, continuesAfter),
        tone: toneOf(g.e.project.id),
        col: g.col,
        span: g.span,
        lane,
        continuesBefore,
        continuesAfter,
        held: g.e.held,
      });
    }
    const more = hidden.flatMap((count, col) => (count > 0 ? [{ col, count }] : []));
    weeks.push({ days, segments, more });
  }

  const itemsOn = (date: string): CalendarDayItem[] =>
    inGrid
      .filter((e) => overlaps(e, date, date))
      .sort((a, b) => (siteRank.get(a.project.id) ?? 0) - (siteRank.get(b.project.id) ?? 0) || a.order - b.order)
      .map((e) => ({
        key: e.key,
        projectId: e.project.id,
        title: projectNameOf(e.project),
        owner: ownerOf(e.project),
        kind: e.kind,
        text: itemText(e, date),
        tone: toneOf(e.project.id),
        held: e.held,
      }));

  // 담당자 — 이달 현장의 담당자 전부(그날 0곳도 보인다). 막대는 이달 가장 많은 담당자 현장 수 기준 · 보류 공정은 그날 일로 세지 않는다.
  const monthLoad = new Map<string, number>();
  for (const p of monthProjects) monthLoad.set(ownerOf(p), (monthLoad.get(ownerOf(p)) ?? 0) + 1);
  const maxLoad = Math.max(1, ...monthLoad.values());
  const selectedItems = itemsOn(selectedDate);
  const owners: CalendarOwnerLoad[] = [...monthLoad.keys()].map((name) => {
    const count = new Set(selectedItems.filter((it) => it.owner === name && !it.held).map((it) => it.projectId)).size;
    return { name, count, ratio: Math.min(1, count / maxLoad), busy: count >= 2 };
  });

  const agenda: CalendarAgendaDay[] = [];
  for (let d = monthStart; d <= monthEnd; d = addDays(d, 1)) {
    const items = itemsOn(d);
    if (items.length > 0) agenda.push({ date: d, title: dayTitle(d), today: d === today, holiday: holidays.get(d) ?? null, items });
  }

  const todayMonth = today.slice(0, 7);
  return {
    month,
    monthTitle: `${Number(month.slice(0, 4))}년 ${Number(month.slice(5, 7))}월`,
    prevMonth: shiftMonth(month, -1),
    nextMonth: shiftMonth(month, 1),
    todayMonth,
    isTodayMonth: month === todayMonth,
    maxLanes,
    weeks,
    selected: {
      date: selectedDate,
      title: dayTitle(selectedDate),
      today: selectedDate === today,
      holiday: holidays.get(selectedDate) ?? null,
      items: selectedItems,
      owners,
    },
    sites: monthProjects.map((p) => ({
      id: p.id,
      title: projectNameOf(p),
      status: p.status,
      owner: ownerOf(p),
      tone: toneOf(p.id),
    })),
    agenda,
  };
};
